import React, { useContext } from 'react';
import { TableColumn } from 'react-data-table-component';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import { formatMoney, formatNumber } from '../../../../utils/utils';
import { Roles } from '../../../../app/shared/enums';
import { AuthContext } from '../../../../contexts/AuthContext';
import { QuoteRouteSegment, QuoteRouteSegmentFilters } from '../../../../app/models/QuoteRouteSegment';
import { Currency } from '../../../../app/models/Currency';
import { Unit } from '../../../../app/models/Unit';
import { Place } from '../../../../app/models/Place';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';

type QuoteRouteSegmentRow = Omit<
    QuoteRouteSegment,
    | 'currency'
    | 'unit'
    | 'origin'
    | 'destiny'
    | 'currency_rate_runner'
    | 'quantity_unit_rate_runner'
    | 'currency_rate_transporter'
    | 'quantity_unit_rate_transporter'
> & {
    currency?: Currency | { name?: string | null } | string | null;
    unit?: Unit | { name?: string | null } | string | null;
    origin?: Place | string | null;
    destiny?: Place | string | null;
    currency_rate_runner?: Currency | { name?: string | null } | string | null;
    quantity_unit_rate_runner?: Unit | { name?: string | null } | string | null;
    currency_rate_transporter?: Currency | { name?: string | null } | string | null;
    quantity_unit_rate_transporter?: Unit | { name?: string | null } | string | null;
    rate_runner?: number | string | null;
    rate_transporter?: number | string | null;
};

interface Props {
    quoteRouteSegments: QuoteRouteSegmentRow[];
    loading?: boolean;
    totalRows: number;
    edit?: (quoteDetailId: number) => void;
    destroy?: (quoteDetailId: number) => void;
    show?: (quoteDetailId: number) => void;
    filter: QuoteRouteSegmentFilters;
    setFilter: (filter: QuoteRouteSegmentFilters) => void;
    paginationRowsPerPageOptions?: number[];
}

const QuoteRouteSegmentDataTable = ({
    quoteRouteSegments,
    totalRows,
    filter,
    setFilter,
    loading = false,
    edit,
    destroy,
    show
}: Props) => {
    const { auth } = useContext(AuthContext);

    const parseNumber = (value: unknown): number => {
        if (value === null || value === undefined || value === '') {
            return 0;
        }

        const numericValue = typeof value === 'string' ? parseFloat(value) : Number(value);

        return Number.isNaN(numericValue) ? 0 : numericValue;
    };

    const getEntityName = (
        entity: Currency | Unit | { name?: string | null } | string | null | undefined
    ): string => {
        if (!entity) {
            return '';
        }

        if (typeof entity === 'string') {
            return entity;
        }

        return entity.name?.toString() ?? '';
    };

    const getPlaceLabel = (place: QuoteRouteSegmentRow['origin']): string => {
        if (!place) {
            return '-';
        }

        if (typeof place === 'string') {
            return place;
        }

        return place.name ?? '-';
    };

    const buildRateWithUnit = (
        rate: number | string | null | undefined,
        currency: Currency | { name?: string | null } | string | null | undefined,
        unit: Unit | { name?: string | null } | string | null | undefined
    ): string => {
        const amount = parseNumber(rate);
        if (!amount) {
            return '-';
        }

        const currencyLabel = getEntityName(currency ?? null) || 'CLP';
        const formattedAmount = formatMoney(amount, currencyLabel);
        const unitLabel = getEntityName(unit ?? null);

        if (!unitLabel) {
            return currencyLabel ? `${formattedAmount} ${currencyLabel}` : formattedAmount;
        }

        return `${formattedAmount} ${currencyLabel} / ${unitLabel}`.trim();
    };

    const columns: TableColumn<QuoteRouteSegmentRow>[] = [
        {
            name: 'Ruta',
            selector: (row: QuoteRouteSegmentRow) => row.id ?? 0,
            sortable: true,
            width: '70px',
            sortField: 'id',
            cell: (row: QuoteRouteSegmentRow) => <span className="">{row.id ?? '-'}</span>
        },
        {
            name: 'Tramo',
            selector: (row: QuoteRouteSegmentRow) => row.route_segment_id ?? 0,
            sortable: true,
            sortField: 'route_segment_id',
            width: '70px',
            cell: (row: QuoteRouteSegmentRow) => <span className="">{row.route_segment_id ?? '-'}</span>
        },
        {
            name: 'Origen',
            selector: (row: QuoteRouteSegmentRow) => getPlaceLabel(row.origin),
            sortable: true,
            sortField: 'origin',
            cell: (row: QuoteRouteSegmentRow) => <span className="">{getPlaceLabel(row.origin)}</span>
        },
        {
            name: 'Destino',
            selector: (row: QuoteRouteSegmentRow) => getPlaceLabel(row.destiny),
            sortable: true,
            sortField: 'destiny',
            cell: (row: QuoteRouteSegmentRow) => <span className="">{getPlaceLabel(row.destiny)}</span>
        },
        {
            name: 'KM real',
            selector: (row: QuoteRouteSegmentRow) => parseNumber(row.distance),
            sortable: true,
            width: '100px',
            sortField: 'distance',
            cell: (row: QuoteRouteSegmentRow) => formatNumber(parseNumber(row.distance))
        },
        {
            name: 'KM Acordado',
            selector: (row: QuoteRouteSegmentRow) => parseNumber(row.agreed_distance),
            sortable: true,
            sortField: 'agreed_distance',
            cell: (row: QuoteRouteSegmentRow) => formatNumber(parseNumber(row.agreed_distance))
        },
        {
            name: 'Tarifa de venta',
            selector: (row: QuoteRouteSegmentRow) => parseNumber(row.rate),
            sortable: true,
            sortField: 'rate',
            cell: (row: QuoteRouteSegmentRow) => buildRateWithUnit(row.rate, row.currency, row.unit)
        },
        {
            name: 'Unidad',
            selector: (row: QuoteRouteSegmentRow) => getEntityName(row.currency) || '',
            sortable: true,
            sortField: 'unit',
            cell: (row: QuoteRouteSegmentRow) => {
                const currencyLabel = getEntityName(row.currency);
                const unitLabel = getEntityName(row.unit);

                if (!currencyLabel && !unitLabel) {
                    return '-';
                }

                if (!unitLabel) {
                    return currencyLabel;
                }

                if (!currencyLabel) {
                    return unitLabel;
                }

                return `${currencyLabel} / ${unitLabel}`;
            }
        },
        {
            name: 'Tarifa Runner',
            selector: (row: QuoteRouteSegmentRow) => parseNumber(row.rate_runner),
            sortable: true,
            sortField: 'rate_runner',
            width: '150px',
            cell: (row: QuoteRouteSegmentRow) => (
                <div className="">{buildRateWithUnit(row.rate_runner, row.currency_rate_runner, row.quantity_unit_rate_runner)}</div>
            )
        },
        {
            name: 'Tarifa Transportista',
            selector: (row: QuoteRouteSegmentRow) => parseNumber(row.rate_transporter),
            sortable: true,
            sortField: 'rate_transporter',
            width: '150px',
            cell: (row: QuoteRouteSegmentRow) => (
                <div className="">{buildRateWithUnit(row.rate_transporter, row.currency_rate_transporter, row.quantity_unit_rate_transporter)}</div>
            )
        },
        {
            name: 'Acciones',
            selector: (row: QuoteRouteSegmentRow) => row.id ?? 0,
            sortable: false,
            width: '135px',
            cell: (row: QuoteRouteSegmentRow) => {
                return (
                    <div className="">
                        {show && (
                            <ButtonTableAction
                                callbackFunction={() => show(row.id ?? -1)}
                                classIcon={'mdi mdi-eye'}
                                colorIcon={'text-info'}
                                errorMessage={'No se puede mostrar este registro.'}
                                title={'Ver'}
                            />
                        )}
                        {edit && (
                            <ButtonTableAction
                                callbackFunction={() => edit(row.id ?? -1)}
                                classIcon={'mdi mdi-pencil'}
                                colorIcon={'text-warning'}
                                errorMessage={'No se puede editar este registro.'}
                                title={'Editar'}
                            />
                        )}
                        {destroy &&
                            auth?.permissions &&
                            (auth?.permissions?.includes('change_quote')) && (
                                <ButtonTableAction
                                    callbackFunction={() => destroy(row.id ?? -1)}
                                    classIcon={'mdi mdi-delete'}
                                    colorIcon={'text-danger'}
                                    errorMessage={'No se puede eliminar este registro.'}
                                    title={'Eliminar'}
                                />
                            )}
                    </div>
                );
            }
        }
    ];

    return (
        <div className="row">
            <div className="col-12 mt-4">
                <DefaultDataTable
                    columns={columns}
                    data={quoteRouteSegments}
                    progressPending={loading}
                    filter={filter}
                    setFilter={setFilter}
                    paginationTotalRows={totalRows}
                />
            </div>
        </div>
    );
};

export default QuoteRouteSegmentDataTable;
